// src/pages/components/Hero.tsx

import React, { useRef } from "react";
import { gsap } from "gsap";
// FIXED PATH: src/pages/components/ to src/constants/ -> ../../constants/strings.json
import strings from "../../constants/strings.json";

interface HeroProps {
    locale?: string;
}

export default function HeroSection({ locale = "en" }: HeroProps): React.ReactElement {
    const textRef = useRef<HTMLSpanElement>(null);

    const localeStrings: any = strings;
    const hero = localeStrings[locale]?.hero ?? localeStrings["en"]?.hero ?? {};

    const handleMouseEnter = () => {
        if (!textRef.current) return;
        gsap.to(textRef.current, {
            scale: 1.05,
            duration: 0.3,
            ease: "power2.out",
        });
    };

    const handleMouseLeave = () => {
        if (!textRef.current) return;
        gsap.to(textRef.current, {
            scale: 1,
            duration: 0.3,
            ease: "power2.inOut",
        });
    };

    return (
        <section className="relative min-h-[90vh] flex items-center overflow-hidden">
            {/* Gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#f0fafa] via-[#e0f2f2] to-[#d0e8e8]"></div>

            {/* Decorative elements - smaller on mobile */}
            <div className="absolute top-10 right-5 md:top-20 md:right-10 w-32 h-32 md:w-56 md:h-56 bg-[#174143]/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-10 left-5 md:bottom-20 md:left-10 w-40 h-40 md:w-72 md:h-72 bg-[#3d9ea4]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

            <div className="relative max-w-7xl mx-auto px-4 pt-28 md:pt-36 pb-12 w-full">
                {/* Welcome badge */}
                <div className="mb-4 md:mb-6 animate-fadeIn">
                    <span className="inline-block px-3 py-1.5 text-[10px] md:text-xs font-bold tracking-widest uppercase rounded-full bg-gradient-to-r from-[#174143] to-[#3d9ea4] text-white shadow-md">
                        {hero.welcome_message || "Welcome"}
                    </span>
                </div>

                <div className="grid lg:grid-cols-2 gap-6 md:gap-10 items-center">
                    {/* Left Content */}
                    <div className="space-y-4 md:space-y-6 animate-fadeIn">
                        {/* Heading - responsive sizes */}
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                            <span className="text-gray-900">
                                {hero.main_heading_part1 || "Transform Your"}{" "}
                            </span>
                            <span
                                ref={textRef}
                                className="inline-block cursor-pointer bg-gradient-to-r from-[#174143] to-[#3d9ea4] bg-clip-text text-transparent"
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                {hero.main_heading_part2 || "Future"}
                            </span>
                        </h1>

                        {/* Description - compact */}
                        <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed max-w-xl">
                            {hero.description || "Excellence in education for Navodaya, SSLC, and competitive exams"}
                        </p>

                        {/* App badge - compact */}
                        <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2 md:py-2.5 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-md border border-[#174143]/10">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#3d9ea4] rounded-full animate-pulse"></div>
                            <span className="text-xs md:text-sm lg:text-base font-semibold">
                                <span className="text-[#174143]">{hero.first_app_label || "NOW AVAILABLE"}</span>
                                <span className="text-gray-700 ml-1 md:ml-2 hidden sm:inline">{hero.first_app_location || "on Play Store"}</span>
                            </span>
                        </div>

                        {/* Google Play button - responsive */}
                        <div className="flex">
                            <a
                                href="https://play.google.com/store/apps/details?id=com.bfts.vidyasamruddhi"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-block rounded-xl md:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                            >
                                <img
                                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                    alt={hero.google_play_badge_alt || "Get it on Google Play"}
                                    className="h-12 sm:h-14 md:h-16 lg:h-18 object-contain"
                                />
                            </a>
                        </div>
                    </div>

                    {/* Right: Hero Image - responsive heights */}
                    <div className="relative animate-fadeIn">
                        {/* Glow */}
                        <div className="absolute inset-0 bg-gradient-to-br from-[#174143] to-[#3d9ea4] rounded-2xl md:rounded-3xl blur-xl md:blur-2xl opacity-20 animate-pulse"></div>

                        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl ring-1 ring-[#174143]/10 hover:shadow-[0_15px_40px_-12px_rgba(23,65,67,0.3)] transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2">
                            <img
                                src="/assets/images/hero.jpg"
                                alt={hero.hero_image_alt || "Hero"}
                                className="w-full h-[300px] sm:h-[350px] md:h-[400px] lg:h-[450px] xl:h-[500px] object-cover"
                            />
                            {/* Gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#174143]/20 via-transparent to-transparent"></div>
                        </div>

                        {/* Floating stats - hidden on small screens */}
                        <div className="hidden sm:block absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white/90 backdrop-blur-md rounded-xl md:rounded-2xl shadow-lg md:shadow-xl p-3 md:p-5 border border-[#174143]/10">
                            <div className="flex items-center gap-2 md:gap-3">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#174143] to-[#3d9ea4] rounded-lg md:rounded-xl flex items-center justify-center">
                                    <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-lg md:text-2xl font-bold text-[#174143]">{hero.stats_value || "200"}</p>
                                    <p className="text-[10px] md:text-sm text-gray-600">{hero.stats_label || "Active Students"}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}